import React, { createContext, useContext, useMemo, useCallback, ReactNode } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { fetchWithSchema } from '@/lib/api';
import { useSchema } from '@/hooks/useSchema';

interface ResolvedFeature {
  enabled: boolean;
  limit: number | null;
  source: 'plan' | 'override';
}

interface FeaturesMap {
  [featureKey: string]: ResolvedFeature;
}

interface TenantFeaturesResponse {
  tenantId: string;
  tenantSchema: string;
  plan: {
    id: string;
    name: string;
  } | null;
  features: FeaturesMap;
}

interface FeaturesContextType {
  features: FeaturesMap;
  plan: TenantFeaturesResponse['plan'];
  loading: boolean;
  error: string | null;
  hasFeature: (featureKey: string) => boolean;
  getFeatureLimit: (featureKey: string) => number | null;
  refresh: () => Promise<void>;
}

const FeaturesContext = createContext<FeaturesContextType | undefined>(undefined);

interface FeaturesProviderProps {
  children: ReactNode;
}

export function FeaturesProvider({ children }: FeaturesProviderProps) {
  const { schema } = useSchema();
  const queryClient = useQueryClient();
  
  // Defer features API call until after LCP to improve performance
  const [shouldLoad, setShouldLoad] = React.useState(false);
  
  React.useEffect(() => {
    if (typeof window === 'undefined' || !schema) return;
    
    // Wait for page to be interactive or use requestIdleCallback
    const loadAfterLCP = () => {
      if (document.readyState === 'complete') {
        setShouldLoad(true);
      } else {
        // Wait for LCP (typically happens within 2.5s)
        const timer = setTimeout(() => {
          setShouldLoad(true);
        }, 2500);
        
        window.addEventListener('load', () => {
          clearTimeout(timer);
          setShouldLoad(true);
        });
        
        return () => {
          clearTimeout(timer);
          window.removeEventListener('load', () => {});
        };
      }
    };
    
    // Use requestIdleCallback if available, otherwise use setTimeout
    if ('requestIdleCallback' in window) {
      requestIdleCallback(() => {
        loadAfterLCP();
      }, { timeout: 3000 });
    } else {
      loadAfterLCP();
    }
  }, [schema]);

  const { data, isLoading, error } = useQuery<TenantFeaturesResponse>({
    queryKey: ['tenant-features', schema],
    queryFn: async () => {
      if (!schema) {
        throw new Error('Schema is required');
      }

      const response = await fetchWithSchema('/api/tenant/features');
      
      if (!response.ok) {
        throw new Error('Failed to load features');
      }

      const data = await response.json();
      
      if (data.success && data.data) {
        return data.data;
      } else {
        throw new Error('Invalid response format');
      }
    },
    enabled: shouldLoad && !!schema, // Defer loading until after LCP and schema is available
    staleTime: 1000 * 60 * 5, // 5 minutes - features don't change often
    gcTime: 1000 * 60 * 30, // 30 minutes - keep in cache
    retry: 2, // Retry 2 times on failure
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000), // Exponential backoff
    refetchOnWindowFocus: false, // Don't refetch on focus for public pages
    refetchOnMount: false, // Don't refetch on mount if deferred
    placeholderData: { features: {}, plan: null } as TenantFeaturesResponse, // Default value
  });

  const features = data?.features || {};
  const plan = data?.plan || null;

  const hasFeature = useCallback((featureKey: string): boolean => {
    return features[featureKey]?.enabled ?? false;
  }, [features]);

  const getFeatureLimit = useCallback((featureKey: string): number | null => {
    return features[featureKey]?.limit ?? null;
  }, [features]);

  const refresh = useCallback(async () => {
    await queryClient.invalidateQueries({ queryKey: ['tenant-features', schema] });
  }, [queryClient, schema]);

  const value = useMemo(() => ({
    features,
    plan,
    loading: isLoading,
    error: error?.message || null,
    hasFeature,
    getFeatureLimit,
    refresh,
  }), [features, plan, isLoading, error, hasFeature, getFeatureLimit, refresh]);

  return (
    <FeaturesContext.Provider value={value}>
      {children}
    </FeaturesContext.Provider>
  );
}

export function useFeatures(): FeaturesContextType {
  const context = useContext(FeaturesContext);
  if (context === undefined) {
    throw new Error('useFeatures must be used within a FeaturesProvider');
  }
  return context;
}


